// src/scenes/groups/index.jsx
import React, { useEffect, useMemo, useState } from "react";
import { Box, useTheme, Snackbar, Alert, Button, Typography } from "@mui/material";
import { DataGrid } from "@mui/x-data-grid";
import Header from "components/Header";
import { useQuery, gql, useMutation } from "@apollo/client";
import AddIcon from "@mui/icons-material/Add";
import { useNavigate } from "react-router-dom";
import GroupForm from "./form";

const GET_GROUPS = gql`
  query {
    groups {
      createdBy
      createdDate
      description
      endDate
      id
      isActive
      logoPath
      name
      startDate
      updatedBy
      updatedDate
    }
  }
`;

const CREATE_GROUP = gql`
  mutation CreateGroup($newGroup: GroupsInput!) {
    createGroup(newGroup: $newGroup) {
      createdBy
      createdDate
      description
      endDate
      id
      isActive
      logoPath
      name
      startDate
      updatedBy
      updatedDate
    }
  }
`;

const Groups = () => {
  const theme = useTheme();
  const navigate = useNavigate();

  // ✅ Restrict access: sys admin only (roleGroupId === 1)
  const isSysAdmin = useMemo(() => localStorage.getItem("roleGroupId") === "1", []);

  useEffect(() => {
    if (!isSysAdmin) {
      navigate("/dashboard", { replace: true });
    }
  }, [isSysAdmin, navigate]);

  const { data, loading, refetch } = useQuery(GET_GROUPS, {
    skip: !isSysAdmin,
    fetchPolicy: "cache-and-network",
  });

  const [createGroup] = useMutation(CREATE_GROUP);

  const [open, setOpen] = useState(false);
  const [successMessage, setSuccessMessage] = useState("");

  const [formData, setFormData] = useState({
    name: "",
    description: "",
    logoPath: null,
  });

  const handleSubmit = async () => {
    try {
      const newGroup = {
        name: formData.name,
        description: formData.description,
        logoPath: formData.logoPath ?? null,
      };

      const { data: resp } = await createGroup({ variables: { newGroup } });

      if (resp?.createGroup?.id) {
        await refetch();
        setSuccessMessage("Group created successfully.");
        setOpen(false);
      } else {
        alert("Failed to create group.");
      }
    } catch (err) {
      console.error(err);
      alert("An error occurred while creating the group.");
    }
  };

  const columns = [
    { field: "id", headerName: "ID", flex: 0.4 },
    { field: "name", headerName: "Name", flex: 1.2 },
    { field: "description", headerName: "Description", flex: 1.6 },
    {
      field: "isActive",
      headerName: "Active",
      flex: 0.6,
      renderCell: (params) => (params.value ? "Yes" : "No"),
    },
    { field: "startDate", headerName: "Start Date", flex: 0.9 },
    { field: "endDate", headerName: "End Date", flex: 0.9 },
    { field: "createdDate", headerName: "Created", flex: 1 },
    { field: "updatedDate", headerName: "Updated", flex: 1 },
  ];

  if (!isSysAdmin) {
    return (
      <Box m="1.5rem 2.5rem">
        <Header title="GROUPS" subtitle="Groups Management" />
        <Typography sx={{ mt: 2 }}>You don’t have permission to access this page.</Typography>
      </Box>
    );
  }

  return (
    <Box m="1.5rem 2.5rem">
      <Header title="GROUPS" subtitle="Manage registered groups" />

      <GroupForm
        open={open}
        onClose={() => setOpen(false)}
        formData={formData}
        setFormData={setFormData}
        onSubmit={handleSubmit}
      />

      <Snackbar
        open={!!successMessage}
        autoHideDuration={4000}
        onClose={() => setSuccessMessage("")}
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
      >
        <Alert onClose={() => setSuccessMessage("")} severity="success" sx={{ width: "100%" }}>
          {successMessage}
        </Alert>
      </Snackbar>

      <Box display="flex" justifyContent="flex-end" mb={2}>
        <Button variant="contained" startIcon={<AddIcon />} onClick={() => setOpen(true)}>
          Create Group
        </Button>
      </Box>

      <Box
        mt="40px"
        height="75vh"
        sx={{
          "& .MuiDataGrid-root": { border: "none" },
          "& .MuiDataGrid-cell": { borderBottom: "none" },
          "& .MuiDataGrid-columnHeaders": {
            backgroundColor: theme.palette.background.alt,
            color: theme.palette.secondary[100],
            borderBottom: "none",
          },
          "& .MuiDataGrid-virtualScroller": {
            backgroundColor: theme.palette.primary.light,
          },
          "& .MuiDataGrid-footerContainer": {
            backgroundColor: theme.palette.background.alt,
            color: theme.palette.secondary[100],
            borderTop: "none",
          },
        }}
      >
        <DataGrid
          loading={loading}
          rows={data?.groups || []}
          getRowId={(row) => row.id}
          columns={columns}
        />
      </Box>
    </Box>
  );
};

export default Groups;
